import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Popover,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  CopyAll,
  Delete,
  Edit,
  FileDownload,
  FileUpload,
  InfoOutlined,
  NavigateNext,
  Refresh,
  Save,
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchShifts,
  upsertShift,
  removeShiftsByShiftId,
  removeShiftEditPoint,
} from '../store/shiftsSlice';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type { Shift, ShiftPayload, ShiftBreak, DaysOfWeek, WorkTimeFraction } from '../types/shifts';

// ─── Constants ─────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = [
  { value: 'none', label: 'None' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
];

const WORK_TIME_FRACTIONS = [
  { value: 'by_hour', label: 'By Hour' },
  { value: 'by_half_hour', label: 'By Half Hour (30 min)' },
  { value: 'by_quarter_hour', label: 'By Quarter Hour (15 min)' },
  { value: 'by_minute', label: 'By Minute' },
];

const ITEM_HEIGHT = 56;
const TIMELINE_HEIGHT = 440;

// ─── Error message sanitizer ────────────────────────────────────────────────

const WTF_CODE_MAP: Record<string, string> = {
  by_quarter_hour: 'by quarter hour',
  by_half_hour: 'by half hour',
  by_hour: 'by hour',
  by_minute: 'by minute',
};

function sanitizeMsg(msg: string): string {
  return msg.replace(/\b(by_quarter_hour|by_half_hour|by_hour|by_minute)\b/g, (m) => WTF_CODE_MAP[m] ?? m);
}

function sanitizeJsonMessage(jm: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(jm).map(([k, v]) => [k, sanitizeMsg(v)]));
}

function emptyPayload(): ShiftPayload {
  return {
    editPointDate: new Date().toISOString().split('T')[0],
    shiftName: '',
    shiftStartTime: '07:30:00',
    workStartTime: '08:00:00',
    workEndTime: '17:00:00',
    shiftEndTime: '03:30:00',
    lateConsideration: 5,
    earlyOutConsideration: 7,
    autoAdjustEarlyInToMin: 0,
    weeklyHoliday: 'friday',
    workTimeFraction: 'by_quarter_hour',
    breaks: [],
  };
}

function shiftToPayload(s: Shift): ShiftPayload {
  return {
    shiftId: s.shiftId,
    editPointDate: s.editPointDate,
    shiftName: s.shiftName,
    shiftStartTime: s.shiftStartTime,
    workStartTime: s.workStartTime,
    workEndTime: s.workEndTime,
    shiftEndTime: s.shiftEndTime,
    lateConsideration: s.lateConsideration,
    earlyOutConsideration: s.earlyOutConsideration,
    autoAdjustEarlyInToMin: s.autoAdjustEarlyInToMin ?? 0,
    weeklyHoliday: s.weeklyHoliday,
    workTimeFraction: s.workTimeFraction,
    breaks: s.breaks.map((b) => ({ ...b })),
  };
}

// ─── Time helpers ───────────────────────────────────────────────────────────

function toTime(val: string): Dayjs | null {
  return val ? dayjs(`1970-01-01T${val}`) : null;
}

function fromTime(d: Dayjs | null): string {
  return d && d.isValid() ? d.format('HH:mm:ss') : '';
}

function isFirstVersion(sorted: Shift[], idx: number): boolean {
  return idx === 0;
}

function isLatestVersion(sorted: Shift[], idx: number): boolean {
  return idx === sorted.length - 1;
}

interface ShiftRow {
  shiftId: string;
  shiftName: string;
  allVersions: Shift[];
  appliedIdx: number;
  applied: Shift | null;
  prev: Shift | null;
  next: Shift | null;
}

const TAG_CHIP_SX = {
  height: 16,
  fontSize: '0.6rem',
  '& .MuiChip-label': { px: 0.75 },
};

// ─── ShiftFormFields ────────────────────────────────────────────────────────

interface ShiftFormFieldsProps {
  value: ShiftPayload;
  onChange: (updated: ShiftPayload) => void;
  errors: Record<string, string>;
  readOnly?: boolean;
  hideEditPointDate?: boolean;
  lockShiftName?: boolean;
}

const ShiftFormFields: React.FC<ShiftFormFieldsProps> = ({
  value,
  onChange,
  errors,
  readOnly = false,
  hideEditPointDate = false,
  lockShiftName = false,
}) => {
  const set = (field: keyof ShiftPayload, val: any) => onChange({ ...value, [field]: val });

  const setBreak = (i: number, field: keyof ShiftBreak, val: string) => {
    const breaks = value.breaks.map((b, idx) => (idx === i ? { ...b, [field]: val } : b));
    onChange({ ...value, breaks });
  };

  const addBreak = () =>
    onChange({ ...value, breaks: [...value.breaks, { breakName: '', breakStartTime: '12:00:00', breakEndTime: '13:00:00' }] });

  const removeBreak = (i: number) =>
    onChange({ ...value, breaks: value.breaks.filter((_, idx) => idx !== i) });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!hideEditPointDate && (
        <TextField
          label="Version Date"
          type="date"
          value={value.editPointDate}
          onChange={(e) => set('editPointDate', e.target.value)}
          size="small"
          disabled={readOnly}
          error={!!errors['editPointDate']}
          helperText={errors['editPointDate']}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
      )}
      <TextField
        label="Shift Name"
        value={value.shiftName}
        onChange={(e) => set('shiftName', e.target.value)}
        size="small"
        disabled={readOnly || lockShiftName}
        error={!!errors['shiftName']}
        helperText={errors['shiftName']}
        fullWidth
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TimePicker
          label="Shift Start Time"
          value={toTime(value.shiftStartTime)}
          onChange={(d) => set('shiftStartTime', fromTime(d))}
          ampm={true}
          views={['hours', 'minutes']}
          disabled={readOnly}
          slotProps={{ textField: { size: 'small', error: !!errors['shiftStartTime'], helperText: errors['shiftStartTime'] } }}
        />
        <TimePicker
          label="Work Start Time"
          value={toTime(value.workStartTime)}
          onChange={(d) => set('workStartTime', fromTime(d))}
          ampm={true}
          views={['hours', 'minutes']}
          disabled={readOnly}
          slotProps={{ textField: { size: 'small', error: !!errors['workStartTime'], helperText: errors['workStartTime'] } }}
        />
        <TimePicker
          label="Work End Time"
          value={toTime(value.workEndTime)}
          onChange={(d) => set('workEndTime', fromTime(d))}
          ampm={true}
          views={['hours', 'minutes']}
          disabled={readOnly}
          slotProps={{ textField: { size: 'small', error: !!errors['workEndTime'], helperText: errors['workEndTime'] } }}
        />
        <TimePicker
          label="Shift End Time"
          value={toTime(value.shiftEndTime)}
          onChange={(d) => set('shiftEndTime', fromTime(d))}
          ampm={true}
          views={['hours', 'minutes']}
          disabled={readOnly}
          slotProps={{ textField: { size: 'small', error: !!errors['shiftEndTime'], helperText: errors['shiftEndTime'] } }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label="Late Consideration (min)"
          type="number"
          value={value.lateConsideration}
          onChange={(e) => set('lateConsideration', Number(e.target.value))}
          size="small"
          disabled={readOnly}
          error={!!errors['lateConsideration']}
          helperText={errors['lateConsideration']}
          inputProps={{ min: -1, max: 120 }}
        />
        <TextField
          label="Early Out Consideration (min)"
          type="number"
          value={value.earlyOutConsideration}
          onChange={(e) => set('earlyOutConsideration', Number(e.target.value))}
          size="small"
          disabled={readOnly}
          error={!!errors['earlyOutConsideration']}
          helperText={errors['earlyOutConsideration']}
          inputProps={{ min: 0 }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label="Auto-Adjust Early In To (Minutes before shift)"
          type="number"
          value={value.autoAdjustEarlyInToMin ?? 0}
          onChange={(e) => set('autoAdjustEarlyInToMin', Number(e.target.value))}
          size="small"
          disabled={readOnly}
          error={!!errors['autoAdjustEarlyInToMin']}
          helperText={errors['autoAdjustEarlyInToMin']}
          inputProps={{ min: 0 }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <FormControl size="small" disabled={readOnly} error={!!errors['weeklyHoliday']}>
          <InputLabel>Weekly Holiday</InputLabel>
          <Select
            label="Weekly Holiday"
            value={value.weeklyHoliday}
            onChange={(e) => set('weeklyHoliday', e.target.value)}
          >
            {DAYS_OF_WEEK.map((d) => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </Select>
          {errors['weeklyHoliday'] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors['weeklyHoliday']}
            </Typography>
          )}
        </FormControl>
        <FormControl size="small" disabled={readOnly} error={!!errors['workTimeFraction']}>
          <InputLabel>Work Time Fraction</InputLabel>
          <Select
            label="Work Time Fraction"
            value={value.workTimeFraction}
            onChange={(e) => set('workTimeFraction', e.target.value)}
          >
            {WORK_TIME_FRACTIONS.map((f) => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </Select>
          {errors['workTimeFraction'] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
              {errors['workTimeFraction']}
            </Typography>
          )}
        </FormControl>
      </Box>

      {/* Breaks */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Breaks</Typography>
          {!readOnly && (
            <Button size="small" startIcon={<Add />} onClick={addBreak}>
              Add Break
            </Button>
          )}
        </Box>
        {value.breaks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>No breaks</Typography>
        )}
        {value.breaks.map((b, i) => (
          <Box
            key={i}
            sx={{
              display: 'grid',
              gridTemplateColumns: readOnly ? '1fr 1fr 1fr' : '1fr 1fr 1fr auto',
              gap: 1,
              mb: 1,
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <TextField
              label="Break Name"
              value={b.breakName}
              onChange={(e) => setBreak(i, 'breakName', e.target.value)}
              size="small"
              disabled={readOnly}
              error={!!errors[`breaks[${i}].breakName`]}
              helperText={errors[`breaks[${i}].breakName`]}
            />
            <TimePicker
              label="Break Start"
              value={toTime(b.breakStartTime)}
              onChange={(d) => setBreak(i, 'breakStartTime', fromTime(d))}
              ampm={true}
              views={['hours', 'minutes']}
              disabled={readOnly}
              slotProps={{ textField: { size: 'small', error: !!errors[`breaks[${i}].breakStartTime`], helperText: errors[`breaks[${i}].breakStartTime`] } }}
            />
            <TimePicker
              label="Break End"
              value={toTime(b.breakEndTime)}
              onChange={(d) => setBreak(i, 'breakEndTime', fromTime(d))}
              ampm={true}
              views={['hours', 'minutes']}
              disabled={readOnly}
              slotProps={{ textField: { size: 'small', error: !!errors[`breaks[${i}].breakEndTime`], helperText: errors[`breaks[${i}].breakEndTime`] } }}
            />
            {!readOnly && (
              <Tooltip title="Remove break">
                <IconButton size="small" onClick={() => removeBreak(i)} color="error" sx={{ mt: 0.5 }}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
        {errors['breaks'] && (
          <Typography variant="caption" color="error">{errors['breaks']}</Typography>
        )}
      </Box>
    </Box>
  );
};

// ─── CreateShiftDialog ──────────────────────────────────────────────────────

interface CreateShiftDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (shift: Shift) => void;
  initialPayload?: ShiftPayload;
}

const CreateShiftDialog: React.FC<CreateShiftDialogProps> = ({ open, onClose, onCreated, initialPayload }) => {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<ShiftPayload>(emptyPayload);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialPayload ? { ...initialPayload, shiftId: undefined, editPointDate: '' } : emptyPayload());
      setErrors({});
    }
  }, [open, initialPayload]);

  const handleClose = useCallback(() => {
    setForm(emptyPayload());
    setErrors({});
    onClose();
  }, [onClose]);

  const handleSubmit = async () => {
    if (!form.editPointDate) {
      setErrors({ editPointDate: 'Version date is required.' });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const res = await apiService.createShift(form);
      onCreated(res.data);
      handleClose();
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(sanitizeJsonMessage(err.jsonMessage));
      } else {
        dispatch(showNotification({ message: sanitizeMsg(err?.message ?? 'Failed to create shift'), severity: 'error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>Create Shift</DialogTitle>
      <DialogContent dividers>
        <ShiftFormFields value={form} onChange={setForm} errors={errors} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <Save />}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── DeleteShiftDialog ──────────────────────────────────────────────────────

interface DeleteShiftDialogProps {
  open: boolean;
  shiftName: string;
  shiftId: string;
  onClose: () => void;
  onDeleted: (shiftId: string) => void;
}

const DeleteShiftDialog: React.FC<DeleteShiftDialogProps> = ({ open, shiftName, shiftId, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await apiService.deleteShift(shiftId);
      onDeleted(shiftId);
      onClose();
    } catch (err: any) {
      setError(err.message ?? 'Failed to delete shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Shift</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>
          Delete all versions for <strong>{shiftName}</strong>? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Delete />}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── ShiftDetailDialog ──────────────────────────────────────────────────────

interface ShiftDetailDialogProps {
  open: boolean;
  shiftId: string;
  initialShiftName: string;
  onClose: () => void;
  onShiftUpdated: (shift: Shift) => void;
  onEditPointDeleted: (shiftId: string, date: string, wasLast: boolean) => void;
}

const ShiftDetailDialog: React.FC<ShiftDetailDialogProps> = ({
  open,
  shiftId,
  initialShiftName,
  onClose,
  onShiftUpdated,
  onEditPointDeleted,
}) => {
  const dispatch = useAppDispatch();
  const [editPoints, setEditPoints] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [editForm, setEditForm] = useState<ShiftPayload>(emptyPayload);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Nested dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [deleteEpOpen, setDeleteEpOpen] = useState(false);
  const [copyToDate, setCopyToDate] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const [addForm, setAddForm] = useState<ShiftPayload>(emptyPayload);
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [addLoading, setAddLoading] = useState(false);
  const [deleteEpLoading, setDeleteEpLoading] = useState(false);
  const [tlScrollOffset, setTlScrollOffset] = useState(0);

  const loadEditPoints = useCallback(async (silent = false) => {
    if (!shiftId) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await apiService.listShiftEditPoints(shiftId);
      const sorted = (res.data ?? []).slice().sort((a, b) => a.editPointDate.localeCompare(b.editPointDate));
      setEditPoints(sorted);
      if (sorted.length > 0 && (!selectedDate || !sorted.find((s) => s.editPointDate === selectedDate))) {
        setSelectedDate(sorted[sorted.length - 1].editPointDate);
      }
    } catch (err: any) {
      dispatch(showNotification({ message: sanitizeMsg(err.message ?? 'Failed to load versions'), severity: 'error' }));
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [shiftId, selectedDate, dispatch]);

  useEffect(() => {
    if (open) {
      setSelectedDate('');
      loadEditPoints();
    }
  }, [open, shiftId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedShift = editPoints.find((ep) => ep.editPointDate === selectedDate) ?? null;

  // When selection changes, initialize form with that version's data
  useEffect(() => {
    if (selectedShift) setEditForm(shiftToPayload(selectedShift));
    setFormErrors({});
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdit = async () => {
    if (!selectedShift) return;
    setSaveLoading(true);
    setFormErrors({});
    try {
      const res = await apiService.updateShift({ ...editForm, shiftId });
      const updated = res.data;
      setEditPoints((prev) =>
        prev.map((ep) => (ep.editPointDate === updated.editPointDate ? updated : ep))
      );
      onShiftUpdated(updated);
      dispatch(showNotification({ message: 'Version updated', severity: 'success' }));
    } catch (err: any) {
      if (err?.jsonMessage) {
        setFormErrors(sanitizeJsonMessage(err.jsonMessage));
      } else {
        dispatch(showNotification({ message: sanitizeMsg(err?.message ?? 'Failed to update version'), severity: 'error' }));
      }
    } finally {
      setSaveLoading(false);
    }
  };

  // Add version
  const openAdd = () => {
    setAddForm({ ...(selectedShift ? shiftToPayload(selectedShift) : emptyPayload()), shiftId, editPointDate: '' });
    setAddErrors({});
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!addForm.editPointDate) {
      setAddErrors({ editPointDate: 'Version date is required.' });
      return;
    }
    setAddLoading(true);
    setAddErrors({});
    try {
      const res = await apiService.createShift({ ...addForm, shiftId });
      const newEp = res.data;
      const newSorted = [...editPoints, newEp].sort((a, b) => a.editPointDate.localeCompare(b.editPointDate));
      setEditPoints(newSorted);
      onShiftUpdated(newEp);
      setSelectedDate(newEp.editPointDate);
      setAddOpen(false);
      dispatch(showNotification({ message: 'Version added', severity: 'success' }));
    } catch (err: any) {
      if (err?.jsonMessage) {
        setAddErrors(sanitizeJsonMessage(err.jsonMessage));
      } else {
        dispatch(showNotification({ message: sanitizeMsg(err?.message ?? 'Failed to add version'), severity: 'error' }));
      }
    } finally {
      setAddLoading(false);
    }
  };

  const submitCopy = async () => {
    setCopyLoading(true);
    try {
      const res = await apiService.copyShiftEditPoint(shiftId, selectedDate, copyToDate);
      const copied = res.data;
      const newSorted = [...editPoints, copied].sort((a, b) => a.editPointDate.localeCompare(b.editPointDate));
      setEditPoints(newSorted);
      onShiftUpdated(copied);
      setSelectedDate(copied.editPointDate);
      setCopyOpen(false);
      dispatch(showNotification({ message: 'Version copied', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: sanitizeMsg(err.message ?? 'Failed to copy version'), severity: 'error' }));
    } finally {
      setCopyLoading(false);
    }
  };

  // Delete version
  const submitDeleteEp = async () => {
    setDeleteEpLoading(true);
    try {
      await apiService.deleteShiftEditPoint(shiftId, selectedDate);
      const remaining = editPoints.filter((ep) => ep.editPointDate !== selectedDate);
      const wasLast = remaining.length === 0;
      onEditPointDeleted(shiftId, selectedDate, wasLast);
      if (wasLast) {
        onClose();
        return;
      }
      setEditPoints(remaining);
      setSelectedDate(remaining[remaining.length - 1].editPointDate);
      setDeleteEpOpen(false);
      dispatch(showNotification({ message: 'Version deleted', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: sanitizeMsg(err.message ?? 'Failed to delete version'), severity: 'error' }));
    } finally {
      setDeleteEpLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{initialShiftName}</Typography>
          <Tooltip title="Refresh versions">
            <IconButton size="small" onClick={() => loadEditPoints(true)} disabled={refreshing}>
              {refreshing ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <Box sx={{ display: 'flex', height: TIMELINE_HEIGHT + 40, overflow: 'hidden' }}>
            {/* Left: timeline */}
            <Box sx={{ width: 220, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary' }}>
                  Versions
                </Typography>
                <Tooltip title="Add Version">
                  <IconButton size="small" onClick={openAdd}><Add fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
              {/* Fades sit outside the scroll container so they don't scroll away */}
              <Box sx={{ position: 'relative', height: TIMELINE_HEIGHT }}>
                <Box
                  onScroll={(e) => setTlScrollOffset(e.currentTarget.scrollTop)}
                  sx={{
                    height: '100%', overflowY: 'auto', userSelect: 'none',
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                    scrollbarWidth: 'thin',
                  }}
                >
                  {/* Upward arrow at top of timeline line */}
                  <Box sx={{
                    position: 'sticky', top: 0, left: 16, transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                    borderBottom: '8px solid', borderBottomColor: '#e0e0e0', zIndex: 0,
                  }} />
                  {editPoints.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No versions</Typography>
                  ) : [...editPoints].reverse().map((ep) => {
                    const isSelected = ep.editPointDate === selectedDate;
                    return (
                      <Box
                        key={ep.editPointDate}
                        onClick={() => setSelectedDate(ep.editPointDate)}
                        sx={{ display: 'flex', alignItems: 'center', height: ITEM_HEIGHT, cursor: 'pointer', '&:hover .date-label': { color: 'primary.main' } }}
                      >
                        {/* Line + dot */}
                        <Box sx={{ width: 32, position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                          <Box sx={{
                            width: isSelected ? 16 : 10, height: isSelected ? 16 : 10,
                            borderRadius: '50%', bgcolor: isSelected ? 'primary.main' : '#bdbdbd',
                            zIndex: 1, transition: 'all 0.15s',
                            border: isSelected ? '2px solid' : 'none', borderColor: 'primary.main',
                          }} />
                        </Box>
                        {/* Date label */}
                        <Typography
                          className="date-label"
                          variant="body2"
                          sx={{ flex: 1, fontWeight: isSelected ? 700 : 400, color: isSelected ? 'primary.main' : 'text.primary', transition: 'color 0.15s', fontSize: '0.82rem' }}
                        >
                          {ep.editPointDate}
                        </Typography>
                        {/* Delete */}
                        <Tooltip title="Delete this version">
                          <IconButton size="small" color="error" sx={{ mr: '10px' }} onClick={(e) => { e.stopPropagation(); setSelectedDate(ep.editPointDate); setDeleteEpOpen(true); }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                  {/* Trailing line below last item */}
                  {editPoints.length > 0 && (
                    <Box sx={{ width: 32, height: 20, position: 'relative', flexShrink: 0 }}>
                      <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                    </Box>
                  )}
                </Box>
                {/* Top fade */}
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                  opacity: Math.min(1, tlScrollOffset / 40), transition: 'opacity 0.15s',
                }} />
                {/* Bottom fade */}
                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                  background: 'linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                  opacity: Math.min(1, (Math.max(0, editPoints.length * ITEM_HEIGHT - TIMELINE_HEIGHT) - tlScrollOffset) / 40),
                  transition: 'opacity 0.15s',
                }} />
              </Box>
            </Box>

            {/* Right: form */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {selectedShift && (
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2">
                    <Box component="span" sx={{ color: 'primary.main' }}>Version</Box>
                    {' | '}{selectedDate}
                  </Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {selectedShift ? (
                  <ShiftFormFields
                    value={editForm}
                    onChange={setEditForm}
                    errors={formErrors}
                    hideEditPointDate
                    lockShiftName
                  />
                ) : (
                  <Typography color="text.secondary">Select a version from the timeline</Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: '15px' }}>
        <Button onClick={onClose}>Close</Button>
        {selectedShift && (
          <Button variant="contained" onClick={saveEdit} disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : <Save />}>
            Save
          </Button>
        )}
      </DialogActions>

      {/* Add Version Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Version</DialogTitle>
        <DialogContent dividers>
          <ShiftFormFields value={addForm} onChange={setAddForm} errors={addErrors} />
        </DialogContent>
        <DialogActions sx={{ p: '20px' }}>
          <Button onClick={() => setAddOpen(false)} disabled={addLoading}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={addLoading}
            startIcon={addLoading ? <CircularProgress size={14} /> : <Save />}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Version Dialog */}
      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Copy Version</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Copy version <strong>{selectedDate}</strong> to a new date:
          </Typography>
          <TextField
            label="Target Date"
            type="date"
            value={copyToDate}
            onChange={(e) => setCopyToDate(e.target.value)}
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)} disabled={copyLoading}>Cancel</Button>
          <Button variant="contained" onClick={submitCopy} disabled={copyLoading || !copyToDate}
            startIcon={copyLoading ? <CircularProgress size={14} /> : <ContentCopy />}>
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Version Dialog */}
      <Dialog open={deleteEpOpen} onClose={() => setDeleteEpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Version</DialogTitle>
        <DialogContent>
          <Typography>
            Delete version <strong>{selectedDate}</strong>?
          </Typography>
          {editPoints.length === 1 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is the last version. Deleting it will remove the shift entirely and cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEpOpen(false)} disabled={deleteEpLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={submitDeleteEp} disabled={deleteEpLoading}
            startIcon={deleteEpLoading ? <CircularProgress size={14} /> : <Delete />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Dialog>
  );
};

// ─── Import / Export Types & Helpers ────────────────────────────────────────

interface ShiftImportCandidate {
  shiftId?: string;
  shiftName: string;
  editPointDate: string;
  shiftStartTime: string;
  workStartTime: string;
  workEndTime: string;
  shiftEndTime: string;
  lateConsideration: number;
  earlyOutConsideration: number;
  autoAdjustEarlyInToMin: number;
  weeklyHoliday: DaysOfWeek;
  workTimeFraction: WorkTimeFraction;
  breaks: ShiftBreak[];
}

interface ExistingShiftItem {
  shiftName: string;
  editPointDate: string;
  reason: string;
}

interface FailedShiftItem {
  shiftName: string;
  editPointDate: string;
  error: string;
}

const normalizeDate = (d: any): string => {
  if (!d || typeof d !== 'string') return '';
  const s = d.trim();
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) {
    const [day, month, year] = s.split('.');
    return `${year}-${month}-${day}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  return s;
};

const normalizeTime = (t: any): string => {
  if (!t || typeof t !== 'string') return '08:00:00';
  const s = t.trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':');
    return `${h.padStart(2, '0')}:${m}:00`;
  }
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(s)) {
    const [h, m, sec] = s.split(':');
    return `${h.padStart(2, '0')}:${m}:${sec}`;
  }
  return s;
};

const normalizeBreaks = (breaks: any): ShiftBreak[] => {
  if (!Array.isArray(breaks)) return [];
  return breaks
    .map((b) => ({
      breakName: String(b.breakName || b.name || '').trim(),
      breakStartTime: normalizeTime(b.breakStartTime || b.start),
      breakEndTime: normalizeTime(b.breakEndTime || b.end),
    }))
    .filter((b) => b.breakName && b.breakStartTime && b.breakEndTime);
};

// ─── ImportShiftsDialog ─────────────────────────────────────────────────────

interface ImportShiftsDialogProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  candidates: ShiftImportCandidate[];
  existing: ExistingShiftItem[];
  imported: Array<{ name: string; date: string }>;
  failed: FailedShiftItem[];
  status: 'preview' | 'importing' | 'completed';
  onImport: () => void;
}

const ImportShiftsDialog: React.FC<ImportShiftsDialogProps> = ({
  open,
  onClose,
  fileName,
  candidates,
  existing,
  imported,
  failed,
  status,
  onImport,
}) => {
  return (
    <Dialog open={open} onClose={status === 'importing' ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Import Shifts
        {fileName && (
          <Typography variant="caption" color="text.secondary" display="block">
            File: {fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {status === 'preview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Total in File: ${candidates.length + existing.length}`} size="small" />
              <Chip
                label={`Ready to Import: ${candidates.length}`}
                size="small"
                color={candidates.length > 0 ? 'success' : 'default'}
              />
              <Chip
                label={`Already Exists / Skipped: ${existing.length}`}
                size="small"
                color={existing.length > 0 ? 'warning' : 'default'}
              />
            </Box>

            {existing.length > 0 && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  The following {existing.length} shift version(s) already exist or cannot be imported and will be skipped:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 150 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Shift Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Version Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existing.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.shiftName}</TableCell>
                          <TableCell>{item.editPointDate}</TableCell>
                          <TableCell>
                            <Chip label={item.reason} size="small" color="warning" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {candidates.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Shifts Ready to Import ({candidates.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Shift Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Version Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Shift Hours</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Work Hours</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Breaks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {candidates.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.shiftName}</TableCell>
                          <TableCell>{item.editPointDate}</TableCell>
                          <TableCell>{item.shiftStartTime.slice(0, 5)} – {item.shiftEndTime.slice(0, 5)}</TableCell>
                          <TableCell>{item.workStartTime.slice(0, 5)} – {item.workEndTime.slice(0, 5)}</TableCell>
                          <TableCell>
                            {item.breaks.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">None</Typography>
                            ) : (
                              item.breaks.map((b, bIdx) => (
                                <Chip
                                  key={bIdx}
                                  label={`${b.breakName}: ${b.breakStartTime.slice(0, 5)}–${b.breakEndTime.slice(0, 5)}`}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.25 }}
                                />
                              ))
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">
                All shifts in this file already exist in the system. There are no new items to import.
              </Alert>
            )}
          </Box>
        )}

        {status === 'importing' && (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="body1">Importing shifts, please wait...</Typography>
            <LinearProgress sx={{ width: '80%' }} />
          </Box>
        )}

        {status === 'completed' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {imported.length > 0 && (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={600}>
                  Successfully imported {imported.length} shift(s):
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 160 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Shift Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Version Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {imported.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                          <TableCell>{item.date}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {existing.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight={600}>
                  {existing.length} shift version(s) already existed and were skipped.
                </Typography>
              </Alert>
            )}

            {failed.length > 0 && (
              <Alert severity="error">
                <Typography variant="body2" fontWeight={600}>
                  {failed.length} shift(s) failed to import:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 140 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Shift Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Version Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failed.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 500 }}>{item.shiftName}</TableCell>
                          <TableCell>{item.editPointDate}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{item.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {status === 'preview' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onImport}
              disabled={candidates.length === 0}
              startIcon={<FileUpload />}
            >
              Import ({candidates.length})
            </Button>
          </>
        )}
        {status === 'completed' && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Shifts (main page) ─────────────────────────────────────────────────────

const Shifts: React.FC = () => {
  const dispatch = useAppDispatch();
  const { shifts, isLoading, error } = useAppSelector((state) => state.shifts);

  const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [infoAnchor, setInfoAnchor] = useState<{ el: HTMLElement; row: ShiftRow } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [clonePayload, setClonePayload] = useState<ShiftPayload | undefined>(undefined);
  const [detailTarget, setDetailTarget] = useState<{ shiftId: string; shiftName: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ shiftId: string; shiftName: string } | null>(null);

  // Import / Export state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [candidatesToImport, setCandidatesToImport] = useState<ShiftImportCandidate[]>([]);
  const [alreadyExistingShifts, setAlreadyExistingShifts] = useState<ExistingShiftItem[]>([]);
  const [importedShifts, setImportedShifts] = useState<Array<{ name: string; date: string }>>([]);
  const [failedShifts, setFailedShifts] = useState<FailedShiftItem[]>([]);
  const [importStatus, setImportStatus] = useState<'preview' | 'importing' | 'completed'>('preview');

  // Derive shift rows with version context for the selected effective date
  const shiftRows = React.useMemo<ShiftRow[]>(() => {
    const groupMap = new Map<string, Shift[]>();
    for (const s of shifts) {
      if (!groupMap.has(s.shiftId)) groupMap.set(s.shiftId, []);
      groupMap.get(s.shiftId)!.push(s);
    }

    return Array.from(groupMap.entries()).map(([shiftId, versions]) => {
      const sorted = versions.slice().sort((a, b) => a.editPointDate.localeCompare(b.editPointDate));

      // Applied: last version where editPointDate <= effectiveDate
      let appliedIdx = -1;
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].editPointDate <= effectiveDate) {
          appliedIdx = i;
          break;
        }
      }

      const applied = appliedIdx >= 0 ? sorted[appliedIdx] : null;
      const prev = appliedIdx > 0 ? sorted[appliedIdx - 1] : null;
      const next = appliedIdx >= 0 && appliedIdx < sorted.length - 1 ? sorted[appliedIdx + 1] : null;

      // Display name from latest version
      const shiftName = sorted[sorted.length - 1].shiftName;

      return { shiftId, shiftName, allVersions: sorted, appliedIdx, applied, prev, next };
    }).sort((a, b) => a.shiftName.localeCompare(b.shiftName));
  }, [shifts, effectiveDate]);

  const handleCreated = (shift: Shift) => {
    dispatch(upsertShift(shift));
    dispatch(showNotification({ message: 'Shift created', severity: 'success' }));
  };

  const handleDeleted = (shiftId: string) => {
    dispatch(removeShiftsByShiftId(shiftId));
    dispatch(showNotification({ message: 'Shift deleted', severity: 'success' }));
  };

  const handleShiftUpdated = (shift: Shift) => {
    dispatch(upsertShift(shift));
  };

  const handleEditPointDeleted = (shiftId: string, date: string, wasLast: boolean) => {
    if (wasLast) {
      dispatch(removeShiftsByShiftId(shiftId));
    } else {
      dispatch(removeShiftEditPoint({ shiftId, editPointDate: date }));
    }
  };

  const handleExport = () => {
    if (shifts.length === 0) {
      dispatch(showNotification({ message: 'No shifts to export', severity: 'warning' }));
      return;
    }

    const exportData = shifts.map((s) => ({
      shiftName: s.shiftName,
      editPointDate: s.editPointDate,
      shiftStartTime: s.shiftStartTime,
      workStartTime: s.workStartTime,
      workEndTime: s.workEndTime,
      shiftEndTime: s.shiftEndTime,
      lateConsideration: s.lateConsideration ?? 0,
      earlyOutConsideration: s.earlyOutConsideration ?? 0,
      autoAdjustEarlyInToMin: s.autoAdjustEarlyInToMin ?? 0,
      weeklyHoliday: s.weeklyHoliday || 'none',
      workTimeFraction: s.workTimeFraction || 'by_quarter_hour',
      breaks: (s.breaks || []).map((b) => ({
        breakName: b.breakName,
        breakStartTime: b.breakStartTime,
        breakEndTime: b.breakEndTime,
      })),
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shifts-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    dispatch(
      showNotification({
        message: `Exported ${exportData.length} shift version(s) successfully`,
        severity: 'success',
      })
    );
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);
        const list: any[] = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.shifts)
          ? parsed.shifts
          : Array.isArray(parsed?.data)
          ? parsed.data
          : parsed && typeof parsed === 'object' && parsed.shiftName
          ? [parsed]
          : null;

        if (!list || list.length === 0) {
          dispatch(
            showNotification({
              message: 'Invalid JSON file: Expected a JSON array of shifts.',
              severity: 'error',
            })
          );
          return;
        }

        const existingKeySet = new Set(
          shifts.map((s) => `${s.shiftName.trim().toLowerCase()}#${s.editPointDate}`)
        );

        const existingNameMap = new Map<string, string>();
        for (const s of shifts) {
          existingNameMap.set(s.shiftName.trim().toLowerCase(), s.shiftId);
        }

        const toImport: ShiftImportCandidate[] = [];
        const existing: ExistingShiftItem[] = [];

        for (const raw of list) {
          if (!raw || typeof raw !== 'object') continue;
          const shiftName = String(raw.shiftName || raw.name || '').trim();
          const editPointDate = normalizeDate(raw.editPointDate || raw.versionDate || raw.date);

          if (!shiftName) continue;
          if (!editPointDate) {
            existing.push({
              shiftName,
              editPointDate: 'N/A',
              reason: 'Missing version date (editPointDate)',
            });
            continue;
          }

          const compositeKey = `${shiftName.toLowerCase()}#${editPointDate}`;
          if (existingKeySet.has(compositeKey)) {
            existing.push({
              shiftName,
              editPointDate,
              reason: 'Shift version already exists in database',
            });
            continue;
          }

          if (toImport.some((c) => c.shiftName.toLowerCase() === shiftName.toLowerCase() && c.editPointDate === editPointDate)) {
            existing.push({
              shiftName,
              editPointDate,
              reason: 'Duplicate entry in imported file',
            });
            continue;
          }

          const matchedShiftId = existingNameMap.get(shiftName.toLowerCase());

          toImport.push({
            shiftId: matchedShiftId,
            shiftName,
            editPointDate,
            shiftStartTime: normalizeTime(raw.shiftStartTime || raw.startTime || '08:00:00'),
            workStartTime: normalizeTime(raw.workStartTime || '08:00:00'),
            workEndTime: normalizeTime(raw.workEndTime || '17:00:00'),
            shiftEndTime: normalizeTime(raw.shiftEndTime || raw.endTime || '17:00:00'),
            lateConsideration: Number(raw.lateConsideration ?? 0),
            earlyOutConsideration: Number(raw.earlyOutConsideration ?? 0),
            autoAdjustEarlyInToMin: Number(raw.autoAdjustEarlyInToMin ?? 0),
            weeklyHoliday: (raw.weeklyHoliday || 'none') as DaysOfWeek,
            workTimeFraction: (raw.workTimeFraction || 'by_quarter_hour') as WorkTimeFraction,
            breaks: normalizeBreaks(raw.breaks),
          });
        }

        setCandidatesToImport(toImport);
        setAlreadyExistingShifts(existing);
        setImportedShifts([]);
        setFailedShifts([]);
        setImportStatus('preview');
        setImportDialogOpen(true);
      } catch (err: any) {
        dispatch(
          showNotification({
            message: 'Failed to parse JSON file: ' + (err?.message || 'Invalid JSON syntax'),
            severity: 'error',
          })
        );
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const handleExecuteImport = async () => {
    setImportStatus('importing');
    const successfullyImported: Array<{ name: string; date: string }> = [];
    const failed: FailedShiftItem[] = [];

    for (const item of candidatesToImport) {
      try {
        const payload: ShiftPayload = {
          shiftId: item.shiftId,
          shiftName: item.shiftName,
          editPointDate: item.editPointDate,
          shiftStartTime: item.shiftStartTime,
          workStartTime: item.workStartTime,
          workEndTime: item.workEndTime,
          shiftEndTime: item.shiftEndTime,
          lateConsideration: item.lateConsideration,
          earlyOutConsideration: item.earlyOutConsideration,
          autoAdjustEarlyInToMin: item.autoAdjustEarlyInToMin,
          weeklyHoliday: item.weeklyHoliday,
          workTimeFraction: item.workTimeFraction,
          breaks: item.breaks,
        };
        const res = await apiService.createShift(payload);
        dispatch(upsertShift(res.data));
        successfullyImported.push({ name: item.shiftName, date: item.editPointDate });
      } catch (err: any) {
        failed.push({
          shiftName: item.shiftName,
          editPointDate: item.editPointDate,
          error: err?.message || 'Failed to create shift',
        });
      }
    }

    setImportedShifts(successfullyImported);
    setFailedShifts(failed);
    setImportStatus('completed');
    dispatch(fetchShifts());
  };

  return (
    <DashboardLayout>
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Scheduling</Typography>
        <Typography variant="body2" color="text.primary" fontWeight={600}>Shifts</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Shifts</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => dispatch(fetchShifts())}>
            <Refresh />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
        <Button
          variant="outlined"
          startIcon={<FileUpload />}
          onClick={() => fileInputRef.current?.click()}
        >
          Import
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleExport}
        >
          Export
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          Create Shift
        </Button>
      </Box>

      {/* Error state */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && (
        <>
          <Box sx={{ mb: 1 }}>
            <TextField
              label="Effective Date"
              type="date"
              size="small"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
          </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Shift Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Schedule</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shiftRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No shifts found. Create your first shift to get started.
                  </TableCell>
                </TableRow>
              )}
              {shiftRows.map((row) => (
                <TableRow key={row.shiftId} hover>
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.shiftName}</Typography>
                      <Tooltip title="Show details">
                        <IconButton
                          size="small"
                          onClick={(e) => setInfoAnchor({ el: e.currentTarget, row })}
                          sx={{ color: 'text.secondary', p: 0.25 }}
                        >
                          <InfoOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  {/* Version column */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {row.prev && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 54, flexShrink: 0 }}>
                            Previous
                          </Typography>
                          <Typography variant="caption">{row.prev.editPointDate}</Typography>
                          {isFirstVersion(row.allVersions, row.appliedIdx - 1) && (
                            <Chip label="First" size="small" sx={TAG_CHIP_SX} />
                          )}
                        </Box>
                      )}
                      {row.applied ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 700, width: 54, flexShrink: 0 }}>
                            Applied
                          </Typography>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                            {row.applied.editPointDate}
                          </Typography>
                          {isFirstVersion(row.allVersions, row.appliedIdx) && (
                            <Chip label="First" size="small" color="primary" sx={TAG_CHIP_SX} />
                          )}
                          {isLatestVersion(row.allVersions, row.appliedIdx) && (
                            <Chip label="Latest" size="small" color="success" sx={TAG_CHIP_SX} />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          Not effective on this date
                        </Typography>
                      )}
                      {row.next && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 54, flexShrink: 0 }}>
                            Next
                          </Typography>
                          <Typography variant="caption">{row.next.editPointDate}</Typography>
                          {isLatestVersion(row.allVersions, row.appliedIdx + 1) && (
                            <Chip label="Latest" size="small" color="success" sx={TAG_CHIP_SX} />
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  {/* Schedule column */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    {row.applied ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 68, flexShrink: 0 }}>
                            Shift
                          </Typography>
                          <Typography variant="caption">
                            {row.applied.shiftStartTime.slice(0, 5)} – {row.applied.shiftEndTime.slice(0, 5)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 68, flexShrink: 0 }}>
                            Work
                          </Typography>
                          <Typography variant="caption">
                            {row.applied.workStartTime.slice(0, 5)} – {row.applied.workEndTime.slice(0, 5)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 68, flexShrink: 0 }}>
                            Holiday
                          </Typography>
                          <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                            {row.applied.weeklyHoliday}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                    <Tooltip title="Clone shift">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (!row.applied) {
                            dispatch(showNotification({ message: 'No effective version on the selected date — adjust the effective date to clone this shift.', severity: 'info' }));
                            return;
                          }
                          setClonePayload(shiftToPayload(row.applied));
                          setCreateOpen(true);
                        }}
                      >
                        <CopyAll fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit shift">
                      <IconButton
                        size="small"
                        onClick={() => setDetailTarget({ shiftId: row.shiftId, shiftName: row.shiftName })}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete shift">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget({ shiftId: row.shiftId, shiftName: row.shiftName })}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* Shift Info Popover */}
      <Popover
        open={!!infoAnchor}
        anchorEl={infoAnchor?.el}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 280, maxWidth: 340 } }}
      >
        {infoAnchor && (() => {
          const { row } = infoAnchor;
          const s = row.applied;
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {row.shiftName}
              </Typography>
              {!s ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Not effective on {effectiveDate}
                </Typography>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Version: {s.editPointDate}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', alignItems: 'baseline' }}>
                    <Typography variant="caption" color="text.secondary">Shift</Typography>
                    <Typography variant="caption">{s.shiftStartTime.slice(0, 5)} – {s.shiftEndTime.slice(0, 5)}</Typography>

                    <Typography variant="caption" color="text.secondary">Work</Typography>
                    <Typography variant="caption">{s.workStartTime.slice(0, 5)} – {s.workEndTime.slice(0, 5)}</Typography>

                    <Typography variant="caption" color="text.secondary">Holiday</Typography>
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{s.weeklyHoliday}</Typography>

                    <Typography variant="caption" color="text.secondary">Time fraction</Typography>
                    <Typography variant="caption">
                      {WORK_TIME_FRACTIONS.find((f) => f.value === s.workTimeFraction)?.label ?? s.workTimeFraction}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">Late grace</Typography>
                    <Typography variant="caption">{s.lateConsideration} min</Typography>

                    <Typography variant="caption" color="text.secondary">Early out grace</Typography>
                    <Typography variant="caption">{s.earlyOutConsideration} min</Typography>

                    <Typography variant="caption" color="text.secondary">Auto-adjust early in</Typography>
                    <Typography variant="caption">{s.autoAdjustEarlyInToMin ?? 0} min</Typography>
                  </Box>

                  {s.breaks.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                        Breaks
                      </Typography>
                      {s.breaks.map((b, i) => (
                        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', mb: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{b.breakName || `Break ${i + 1}`}</Typography>
                          <Typography variant="caption">{b.breakStartTime.slice(0, 5)} – {b.breakEndTime.slice(0, 5)}</Typography>
                        </Box>
                      ))}
                    </>
                  )}
                </>
              )}
            </Box>
          );
        })()}
      </Popover>

      {/* Create Shift Dialog */}
      <CreateShiftDialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setClonePayload(undefined); }}
        onCreated={handleCreated}
        initialPayload={clonePayload}
      />

      {/* Delete Shift Dialog */}
      {deleteTarget && (
        <DeleteShiftDialog
          open
          shiftId={deleteTarget.shiftId}
          shiftName={deleteTarget.shiftName}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(id) => {
            handleDeleted(id);
            setDeleteTarget(null);
          }}
        />
      )}

      {/* Shift Detail Dialog */}
      {detailTarget && (
        <ShiftDetailDialog
          open
          shiftId={detailTarget.shiftId}
          initialShiftName={detailTarget.shiftName}
          onClose={() => setDetailTarget(null)}
          onShiftUpdated={handleShiftUpdated}
          onEditPointDeleted={handleEditPointDeleted}
        />
      )}

      {/* Import Shifts Dialog */}
      <ImportShiftsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        fileName={importFileName}
        candidates={candidatesToImport}
        existing={alreadyExistingShifts}
        imported={importedShifts}
        failed={failedShifts}
        status={importStatus}
        onImport={handleExecuteImport}
      />

    </Box>
    </DashboardLayout>
  );
};

export default Shifts;
